/**
 * useTableFilters Hook
 *
 * Manages filter and sort state for DataTable components.
 * Provides utilities for converting filter state to API params.
 */

import { useState, useCallback, useMemo } from 'react';
import { ActiveFilters, SortConfig } from '@/components/shared/DataTable/types';
import {
  getDateRangeFromFilter,
  getSortParam,
  CustomDateRange,
} from '@/components/shared/DataTable/filterConfigs';
import { DateRangePreset } from '@/components/shared/DataTable';

export interface UseTableFiltersOptions {
  /** Initial filter values */
  initialFilters?: ActiveFilters;
  /** Initial sort configuration */
  initialSort?: SortConfig;
  /** Initial date preset */
  initialDatePreset?: DateRangePreset;
  /** Callback when filters change (e.g., to reset pagination) */
  onFiltersChange?: () => void;
  /** Callback when sort changes */
  onSortChange?: () => void;
}

export interface UseTableFiltersResult {
  /** Current active filters */
  activeFilters: ActiveFilters;
  /** Set filters */
  setFilters: (filters: ActiveFilters) => void;
  /** Clear all filters */
  clearFilters: () => void;
  /** Current sort configuration */
  sort: SortConfig;
  /** Set sort */
  setSort: (sort: SortConfig) => void;
  /** Date range preset */
  datePreset: DateRangePreset;
  /** Set date preset */
  setDatePreset: (preset: DateRangePreset) => void;
  /** Custom date range */
  customDateRange?: CustomDateRange;
  /** Set custom date range */
  setCustomDateRange: (range?: CustomDateRange) => void;
  /** Handle date range change from DateRangePicker */
  handleDateRangeChange: (preset: DateRangePreset, range?: CustomDateRange) => void;
  /** Get API params from current filter state */
  getApiParams: () => Record<string, string | undefined>;
  /** Get sort param string for API */
  getSortApiParam: () => string | undefined;
  /** Check if any filters are active */
  hasActiveFilters: boolean;
}

/**
 * Hook to manage table filter and sort state
 */
export function useTableFilters(options: UseTableFiltersOptions = {}): UseTableFiltersResult {
  const {
    initialFilters = {},
    initialSort = { key: null, direction: null },
    initialDatePreset = 'all',
    onFiltersChange,
    onSortChange,
  } = options;

  // Filter state
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(initialFilters);

  // Sort state
  const [sort, setSort] = useState<SortConfig>(initialSort);

  // Date range state
  const [datePreset, setDatePresetInternal] = useState<DateRangePreset>(initialDatePreset);
  const [customDateRange, setCustomDateRangeInternal] = useState<CustomDateRange | undefined>(
    undefined
  );

  // Set filters with callback
  const setFilters = useCallback(
    (filters: ActiveFilters) => {
      setActiveFilters(filters);
      onFiltersChange?.();
    },
    [onFiltersChange]
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    setActiveFilters({});
    setDatePresetInternal('all');
    setCustomDateRangeInternal(undefined);
    onFiltersChange?.();
  }, [onFiltersChange]);

  // Set sort with callback
  const handleSetSort = useCallback(
    (newSort: SortConfig) => {
      setSort(newSort);
      onSortChange?.();
    },
    [onSortChange]
  );

  // Set date preset
  const setDatePreset = useCallback(
    (preset: DateRangePreset) => {
      setDatePresetInternal(preset);
      if (preset !== 'custom') {
        setCustomDateRangeInternal(undefined);
      }
      onFiltersChange?.();
    },
    [onFiltersChange]
  );

  // Set custom date range
  const setCustomDateRange = useCallback(
    (range?: CustomDateRange) => {
      setCustomDateRangeInternal(range);
      if (range) {
        setDatePresetInternal('custom');
      }
      onFiltersChange?.();
    },
    [onFiltersChange]
  );

  // Handle date range change from DateRangePicker
  const handleDateRangeChange = useCallback(
    (preset: DateRangePreset, range?: CustomDateRange) => {
      setDatePresetInternal(preset);
      setCustomDateRangeInternal(range);
      onFiltersChange?.();
    },
    [onFiltersChange]
  );

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    const hasBasicFilters = Object.values(activeFilters).some(
      (value) => value !== undefined && value !== '' && value !== 'all'
    );
    const hasDateFilter = datePreset !== 'all';
    return hasBasicFilters || hasDateFilter;
  }, [activeFilters, datePreset]);

  // Get API params from filter state
  const getApiParams = useCallback((): Record<string, string | undefined> => {
    const params: Record<string, string | undefined> = {};

    // Handle date range filter
    if (datePreset !== 'all') {
      const dateRange = getDateRangeFromFilter(datePreset, customDateRange);
      if (dateRange.dateStart) params['dateStart'] = dateRange.dateStart;
      if (dateRange.dateEnd) params['dateEnd'] = dateRange.dateEnd;
    }

    // Handle other filters
    Object.entries(activeFilters).forEach(([key, value]) => {
      if (value === undefined || value === '' || value === 'all') return;

      // Skip dateRange key since we handle it above
      if (key === 'dateRange') return;

      if (key === 'actor' && typeof value === 'string') {
        // Doctor filter - needs Practitioner/ prefix
        params['actor'] = `Practitioner/${value}`;
      } else if (typeof value === 'string') {
        params[key] = value;
      } else if (Array.isArray(value)) {
        params[key] = value.join(',');
      }
    });

    return params;
  }, [activeFilters, datePreset, customDateRange]);

  // Get sort API param
  const getSortApiParam = useCallback((): string | undefined => {
    return getSortParam(sort.key, sort.direction);
  }, [sort]);

  return {
    activeFilters,
    setFilters,
    clearFilters,
    sort,
    setSort: handleSetSort,
    datePreset,
    setDatePreset,
    customDateRange,
    setCustomDateRange,
    handleDateRangeChange,
    getApiParams,
    getSortApiParam,
    hasActiveFilters,
  };
}

export default useTableFilters;
